import type { AriaRole } from 'react';
import React, { useRef } from 'react';

// Prevent the following from being overridden, as doing so would break this component's functionality.
type DisallowedProps = 'tabIndex' | 'onKeyDown';

export type Props = Omit<
  {
    role?: AriaRole;
  } & React.HTMLAttributes<HTMLDivElement>,
  DisallowedProps
>;

// A <div> container which includes functional accessibility features (setting role="button" and tabindex=0 to make
// it tab-able, and clickable using enter and spacebar). Note that using interactive HTML elements (e.g. <button>)
// should generally be preferred for button components, as those automatically provide the functionality provided here.
// However, since <div> cannot be used inside of a <button>, this component provides the flexibility for custom
// clickable component layouts if needed.
// NOTE: this component provides no styling (not even cursor: pointer).
const InteractiveDiv: React.FC<Props> = (props) => {
  const elementRef = useRef<HTMLDivElement>(null);

  const { role = 'button', ...rest } = props;

  return (
    <div
      {...rest}
      // prevent input props from overriding any of the below, as doing so would break this component's functionality
      ref={elementRef}
      role={role}
      // needed because eslint doesn't know that an interactive aria-role value type (e.g. "button" is being passed)
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      onKeyDown={(e) => {
        // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role#toggle_button_example
        // Check to see if space or enter were pressed
        // "Spacebar" for IE11 support
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar') {
          elementRef.current?.click();
          // Note: although the MDN docs include `event.preventDefault()` here, we don't want to do that in case we have
          // nested components (e.g. button group within a radio option), and the parent component absorbs the event.
        }
      }}
    />
  );
};

export default InteractiveDiv;
